import React, { useCallback, useEffect, useState } from 'react'
import Module from '../../../../layout/Module'
import { Box } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import useSubjects from '../../../../../hooks/subjects/useSubjects'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import { getSubjectById } from '../../../../../services/subjects/subjects.service'
import {
  updateSubjectError,
  updateSubjectForm,
  updateSubjectFormField
} from '../../../../../store/actions/subjects.actions'
import { handlerErrorV2 } from '../../../../../utils/errors'
import { Alert } from '@mui/material'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import DialogAction from '../Dialog/Dialog'
import Dialog from '../../../../dialog/Dialog'
import isEmpty from 'lodash/isEmpty'
import { toggleAlertModule } from '../../../../../store/actions/alert.actions'
import Form from '../Form/Form'

const Edit = () => {
  const dispatch = useDispatch()
  const { updateSubject, isLoading } = useSubjects()
  const formData = useSelector((state) => state?.subject?.form || {})
  const permission = useSelector((state) => state?.session?.permissions?.subject || {})
  const alertSave = useSelector((state) => state?.alert?.save || {})

  const { curriculumId, id } = useParams()
  const [error, setError] = useState(null)
  const [show, setShow] = useState(false)
  const [modal, setModal] = useState(null)
  const navigate = useNavigate()

  const [loading, toggleLoading] = useState(false)
  const [isLoadingSave, toggleIsLoadingSave] = useState(false)

  const onCancel = () => {
    navigate(`/dashboard/subjects/${curriculumId}`)
  }
  const onChangeAlert = (key, value) => {
    dispatch(updateSubjectFormField(key, value))
    if (key === 'curriculumId') {
      dispatch(updateSubjectFormField('curriculumElements', {}))
      dispatch(updateSubjectFormField('curriculumelements', {}))
    }
    if (!alertSave.mode || !alertSave.onSave) {
      dispatch(toggleAlertModule(false, 'Subjects', 'edit', onSave, alertSave.onDontSave))
    }
  }

  const onSave = async (e) => {
    setError(null)
    e.preventDefault()
    try {
      toggleIsLoadingSave(true)
      let result = await updateSubject(id, formData)
      if (result.error) {
        const { message, error, ...rest } = result
        if (message) {
          setError({ message })
        } else {
          dispatch(updateSubjectError(rest))
        }
      }
    } finally {
      toggleIsLoadingSave(false)
    }
  }

  const onLoad = useCallback(async () => {
    if (id) {
      dispatch(updateSubjectError({}))

      toggleLoading(true)
      try {
        const response = await getSubjectById(curriculumId, id)
        if (!response?.data && response?.error) {
          setError({
            message: response.error.message || 'An error ocurred'
          })
        } else {
          const formData = response?.data || {}
          dispatch(updateSubjectForm(formData))
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        setError(message)
      } finally {
        toggleLoading(false)
      }
    }
  }, [curriculumId, id])

  useEffect(() => {
    onLoad()
  }, [onLoad])

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary flex justify-between'>
        <div />
        <div className='flex items-center justify-end gap-2'>
          {permission.archived && (
            <IconTooltipButton
              id={`archive-${id}`}
              disabled={loading || isLoadingSave || isLoading}
              title='Archive'
              icon='archive'
              style={{
                borderRadius: '0.5rem',
                backgroundColor: 'var(--destructive)',
                color: '#fff',
                padding: '0.45rem'
              }}
              className='shadow-sm'
              onClick={() => setModal({ type: 'archive', form: formData })}
            />
          )}
          {permission.deleted && (
            <IconTooltipButton
              id={`delete-${id}`}
              disabled={loading || isLoadingSave || isLoading}
              title='Delete'
              icon='trash-alt'
              style={{
                borderRadius: '0.5rem',
                backgroundColor: '#fff',
                color: 'var(--destructive)',
                padding: '0.45rem'
              }}
              className='shadow-sm'
              onClick={() => setModal({ type: 'delete', form: formData })}
            />
          )}
          <div className='px-4' />
          {permission.preview && (
            <IconTooltipButton
              disabled={loading || isLoadingSave || isLoading}
              title='Preview'
              icon='expand-alt'
              style={{
                borderRadius: '0.5rem',
                backgroundColor: 'var(--primary)',
                color: 'var(--primary-foreground)',
                padding: '0.45rem'
              }}
              className='shadow-sm'
              onClick={() => setShow(true)}
            />
          )}
          {permission.cloned && (
            <IconTooltipButton
              disabled={loading || isLoadingSave || isLoading}
              title='Clone'
              icon='clone'
              color='secondary'
              style={{
                borderRadius: '0.5rem',
                color: 'var(--primary)',
                padding: '0.45rem'
              }}
              className='shadow-sm'
              onClick={() => setModal({ type: 'clone', form: formData })}
            />
          )}
          <div className='px-1' />
          <AlertSaveButton
            submodule='Subjects'
            disabled={loading || isLoadingSave || isLoading}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm w-full'
            onDontSave={onCancel}
          />
          {permission.updated && (
            <ButtonStartIcon
              disabled={loading || isLoadingSave || isLoading}
              loading={isLoadingSave}
              onClick={onSave}
              startIcon={['fal', 'check']}
              color='primary'
              className='w-full'
            >
              Save
            </ButtonStartIcon>
          )}
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer
        loading={isLoadingSave || isLoading}
        classNameWrapper={isLoadingSave || isLoading || loading ? '' : 'overflow-auto'}
      >
        {!loading && (
          <Box className={['absolute inset-0 pb-2 pt-6 px-4'].join(' ')}>
            {error?.message && (
              <Alert severity='error' className='mb-4' onClose={() => setError(null)}>
                {error?.message}
              </Alert>
            )}
            <Form onChangeAlert={onChangeAlert} type='update' />
          </Box>
        )}
        {modal && (
          <DialogAction
            show={!!modal}
            onClose={() => setModal(null)}
            type={modal.type}
            formData={modal.form}
          />
        )}
        {show && (
          <Dialog
            id={`subject-preview-${id}`}
            show={show}
            title='Subject preview'
            onClose={() => setShow(false)}
            onCloseHeader={true}
            maxWidth='sm'
          >
            <Form isPreview={true} />
          </Dialog>
        )}
      </Module.BodyContainer>
    </>
  )
}

export default Edit
